import type { Viewer, ViewerConfig } from "@photo-sphere-viewer/core";
import type { AutorotatePluginConfig } from "@photo-sphere-viewer/autorotate-plugin";
import "@photo-sphere-viewer/core/index.css";
import { FC, css } from "dreamland/core";
import { getWebpPath } from "../lib/images";

interface PhotoSphereProps {
	src: string;
	fallback?: string;
	viewerConfig?: Partial<
		Omit<ViewerConfig, "container" | "panorama" | "plugins">
	>;
	autorotateConfig?: Partial<AutorotatePluginConfig>;
}

export function PhotoSphere(this: FC<PhotoSphereProps>) {
	let viewer: Viewer | null = null;

	this.cx.mount = () => {
		void (async () => {
			const container = this.root.querySelector(
				".photo-sphere-viewer"
			) as HTMLElement;
			const fallbackEl = this.root.querySelector(
				".photo-sphere-fallback"
			) as HTMLElement;
			const hintEl = this.root.querySelector(
				".photo-sphere-hint"
			) as HTMLElement;

			if (container) {
				const [{ Viewer }, { AutorotatePlugin }] = await Promise.all([
					import("@photo-sphere-viewer/core"),
					import("@photo-sphere-viewer/autorotate-plugin"),
				]);

				viewer = new Viewer({
					navbar: false,
					mousewheel: false,
					...this.viewerConfig,
					container,
					panorama: this.src,
					plugins: [
						[
							AutorotatePlugin,
							{
								...this.autorotateConfig,
							},
						],
					],
				});

				viewer.addEventListener("ready", () => {
					if (fallbackEl) {
						fallbackEl.style.display = "none";
					}
					container.style.opacity = "1";

					if (hintEl) {
						hintEl.classList.add("visible");
						setTimeout(() => {
							hintEl.classList.remove("visible");
						}, 3000);
					}
				});
			}
		})();
	};

	const isMobile =
		typeof navigator !== "undefined" &&
		/iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
	const hintText = isMobile
		? "Drag to look around"
		: "Click and drag to look around";

	const fallbackSrc = this.fallback ?? this.src;
	const fallbackWebp = getWebpPath(fallbackSrc);

	return (
		<div class="photo-sphere">
			<picture>
				{fallbackWebp ? (
					<source type="image/webp" srcset={fallbackWebp} />
				) : (
					false
				)}
				<img
					class="photo-sphere-fallback"
					src={fallbackSrc}
					alt="360° panorama"
				/>
			</picture>
			<div class="photo-sphere-viewer"></div>
			<div class="photo-sphere-hint">
				<span class="material-symbols">panorama_photosphere</span>
				<span>{hintText}</span>
			</div>
		</div>
	);
}

PhotoSphere.style = css`
	:scope {
		position: relative;
		width: 100%;
		min-height: 400px;
		overflow: hidden;
	}

	.photo-sphere-fallback {
		position: absolute;
		top: 0;
		left: 50%;
		transform: translateX(-50%);
		height: 100%;
		min-width: 100%;
		object-fit: cover;
	}

	.photo-sphere-viewer {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
		opacity: 0;
	}

	.photo-sphere-hint {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		font-size: 1.75rem;
		font-family: var(--font-display);
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.5em;
		padding: 0.75rem 1rem;
		background: rgba(0, 0, 0, 0.7);
		color: white;
		border-radius: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		opacity: 0;
		transition: opacity 1s ease-out;
		z-index: 10;
	}

	.photo-sphere-hint.visible {
		opacity: 1;
	}

	.photo-sphere-hint .material-symbols {
		font-size: 3rem;
	}
`;

export default PhotoSphere;
